import React, { useEffect, useState } from 'react';
import { Fade } from '../Fade';
import { PaymentForm } from '../PaymentForm';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { useUser } from '../../shared/use-user';
import { useAuthentication } from '../../shared/use-authentication';
import { useRouting } from '../../shared/use-routing';
import { getLegalLinks } from '../../app-ui-config';
import { renderAgreementTerms } from '../../shared/render-agreement-terms';
import { pagePathnames } from '../../pages/page-routes';
import styles from './Checkout.css';

function Checkout({ subscriptionData, merchantData, onClose, tryAgain, isPending }) {
    useEffect(() => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, []);

    const [errorData, setErrorData] = useState({});
    const [isSuccessful, setIsSuccessful] = useState(false);
    const { updateUserData } = useAuthentication();
    const { buildPath, lang } = useRouting();
    const { userData } = useUser();
    const legalLinks = getLegalLinks(userData.legalRegion, lang);

    useEffect(() => {
        if (isSuccessful) {
            updateUserData();
        }
    }, [isSuccessful]);

    const handleOnTryAgain = async () => {
        await tryAgain(subscriptionData, merchantData.subscriptionId);
        setErrorData({});
    };

    if (errorData.isModalVisible) {
        return (
            <Fade
                duration={500}
                triggerOnce
            >
                <div className={`${styles.root} ${styles.error}`}>
                    <div className={styles.header}>
                        <h1 className={styles.title}>
                            {errorData.title}
                        </h1>
                        <Button
                            type='secondary'
                            iconLeft={{ name: 'close' }}
                            onClick={onClose}
                            size='small'
                        />
                    </div>
                    <div className={styles.noticeBlock}>
                        <Icon name='info' />
                        <p>{errorData.description}</p>
                    </div>
                    <div className={styles.helpBlock}>
                        <p>What to do:</p>
                        <p>{errorData.helpDescription}</p>
                    </div>
                    <div className={styles.buttons}>
                        <Button
                            label='Try again'
                            onClick={handleOnTryAgain}
                            size='medium'
                            isLoading={isPending}
                        />
                        <Button
                            label='Decline'
                            onClick={onClose}
                            type='secondary'
                            size='medium'
                        />
                    </div>
                </div>
            </Fade>
        );
    }

    if (isSuccessful) {
        return (
            <Fade
                duration={500}
                triggerOnce
            >
                <div className={`${styles.root} ${styles.successful}`}>
                    <div className={styles.header}>
                        <h1 className={styles.title}>
                            Thank you for your purchase!
                        </h1>
                        <Button
                            type='secondary'
                            iconLeft={{ name: 'close' }}
                            size='small'
                            link={{
                                to: buildPath(pagePathnames.dashboard)
                            }}
                        />
                    </div>
                    <div className={styles.successfulBlock}>
                        <h2>{subscriptionData.name}</h2>
                        <p className={styles.confirmedText}>Payment order confirmed</p>
                        <p>
                            You now have all the benefits of a premium subscription. <br /><br />
                            <strong>Time to start talking — your next memory is just a question away.</strong>
                        </p>
                    </div>
                    <Button
                        label='Close'
                        className={styles.buttonSuccessful}
                        size='medium'
                        link={{
                            to: buildPath(pagePathnames.dashboard)
                        }}
                    />
                </div>
            </Fade>
        );
    }

    return (
        <Fade
            duration={500}
            triggerOnce
        >
            <div className={styles.root}>
                <div className={styles.header}>
                    <h1 className={styles.title}>
                        Complete your checkout
                    </h1>
                    <Button
                        type='secondary'
                        iconLeft={{ name: 'close' }}
                        onClick={onClose}
                        size='small'
                    />
                </div>
                <div className={styles.wrapper}>
                    <div className={styles.summaryBlock}>
                        <h2>Your Order Summary</h2>
                        <div className={styles.totalRow}>
                            <span>Total today:</span>
                            <span>{subscriptionData.trial?.price || subscriptionData.price}</span>
                        </div>
                        <div className={styles.divider} />
                        <div className={styles.totalRow}>
                            <span>After {subscriptionData.billingPeriod.value} {subscriptionData.billingPeriod.unit}s (auto-renews):</span>
                            <span>{subscriptionData.price}</span>
                        </div>
                        <p className={styles.agreementTermsText}>
                            {renderAgreementTerms(subscriptionData, legalLinks)}
                        </p>
                    </div>
                    <PaymentForm
                        merchantData={merchantData}
                        setErrorData={setErrorData}
                        setIsSuccessful={setIsSuccessful}
                    />
                </div>
            </div>
        </Fade>
    );
}

export {
    Checkout
};
